import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import logo from '../assets/logo.png';
import zeina from '../assets/reference/zeina.png';
import useReducedMotion from '../hooks/useReducedMotion';
import useScrollLock from '../hooks/useScrollLock';
import './preloader.css';

export default function Preloader({ onComplete }) {
  const root = useRef(null);
  const reduced = useReducedMotion();
  useScrollLock(true);

  useLayoutEffect(() => {
    let cancelled = false;
    let completed = 0;
    let finished = false;
    const timers = [];
    const images = [];
    const started = performance.now();
    const progress = { value: 0 };
    const context = gsap.context(() => {}, root);
    context.add('progress', (value) => {
      gsap.to(progress, { value, duration: reduced ? 0 : .35, overwrite: true,
        onUpdate: () => {
          if (!root.current) return;
          root.current.querySelector('.preloader__number').textContent = String(Math.round(progress.value)).padStart(3, '0');
          root.current.querySelector('.preloader__progress').style.transform = `scaleX(${progress.value / 100})`;
        },
      });
    });
    context.add('exit', () => {
      gsap.timeline({ onComplete })
        .to('.preloader__content', { y: reduced ? 0 : -30, autoAlpha: 0, duration: reduced ? .08 : .4 })
        .to(root.current, { yPercent: reduced ? 0 : -100, autoAlpha: reduced ? 0 : 1, duration: reduced ? .08 : .85, ease: 'power4.inOut' }, reduced ? 0 : .18);
    });
    const finish = () => {
      if (cancelled || finished) return;
      finished = true;
      context.progress(100);
      const remaining = reduced ? 0 : Math.max(450, 1200 - (performance.now() - started));
      timers.push(setTimeout(() => { if (!cancelled) context.exit(); }, remaining));
    };
    const settle = () => {
      if (cancelled || finished) return;
      completed += 1; context.progress(Math.round(completed / 3 * 100));
      if (completed === 3) finish();
    };
    [logo, zeina].forEach((src) => {
      const image = new Image(); images.push(image);
      image.onload = async () => {
        try { await image.decode?.(); } catch { /* A decoded fallback is not required to enter. */ }
        settle();
      };
      image.onerror = settle;
      image.src = src;
    });
    Promise.resolve(document.fonts?.ready).then(settle, settle);
    // Never leave the page locked if an image or font request stalls.
    timers.push(setTimeout(finish, 4000));
    return () => {
      cancelled = true; timers.forEach(clearTimeout);
      images.forEach((image) => { image.onload = null; image.onerror = null; });
      context.revert();
    };
  }, [onComplete, reduced]);

  return (
    <div className="preloader" ref={root} role="status" aria-label="Loading Zeina's portfolio" aria-live="polite">
      <div className="preloader__content" aria-hidden="true">
        <img src={logo} alt="" className="preloader__logo" />
        <p className="preloader__eyebrow">DESIGN WITH FEELING.</p>
        <div className="preloader__count"><span className="preloader__number">000</span><span>%</span></div>
        <div className="preloader__track"><span className="preloader__progress" /></div>
        <div className="preloader__caption"><span>LOADING PORTFOLIO</span><span>ZEINA TAMER</span></div>
      </div>
    </div>
  );
}
